import { createRoot } from "react-dom/client";
import { HelmetProvider } from 'react-helmet-async';
import App from "./App.tsx";
import "./index.css";
import { CartProvider } from "./contexts/CartContext";
import { initSentry } from "./lib/sentry";

// Wrap Sentry initialization in try-catch to prevent blank screen
try {
  initSentry();
} catch (e) {
  console.error("[Init] Sentry initialization failed:", e);
}

// Boot timeout fallback - shows message if app doesn't render within 10 seconds
const BOOT_TIMEOUT_MS = 10000;
let bootTimeoutId: ReturnType<typeof setTimeout> | null = null;

const showBootFallback = () => {
  const rootElement = document.getElementById("root");
  if (rootElement && rootElement.children.length === 0) {
    rootElement.innerHTML = `
      <div style="min-height: 100vh; display: flex; align-items: center; justify-content: center; flex-direction: column; font-family: system-ui, -apple-system, sans-serif; padding: 20px; text-align: center; background: #fafafa;">
        <div style="max-width: 400px;">
          <h1 style="font-size: 1.5rem; margin-bottom: 12px; color: #1a1a1a;">Taking longer than expected</h1>
          <p style="color: #666; margin-bottom: 24px; line-height: 1.5;">
            The page is still loading. This might be due to a slow connection or a temporary issue.
          </p>
          <div style="display: flex; gap: 12px; justify-content: center; flex-wrap: wrap;">
            <button onclick="window.location.reload()" style="padding: 12px 24px; background: #1a1a1a; color: white; border: none; border-radius: 8px; cursor: pointer; font-size: 14px;">
              Reload Page
            </button>
            <button onclick="localStorage.clear(); sessionStorage.clear(); window.location.reload()" style="padding: 12px 24px; background: white; color: #1a1a1a; border: 1px solid #ddd; border-radius: 8px; cursor: pointer; font-size: 14px;">
              Clear Cache & Reload
            </button>
          </div>
        </div>
      </div>
    `;
  }
};

const clearBootTimeout = () => {
  if (bootTimeoutId) {
    clearTimeout(bootTimeoutId);
    bootTimeoutId = null;
  }
};

// Start boot timeout
bootTimeoutId = setTimeout(showBootFallback, BOOT_TIMEOUT_MS);

// Clear timeout once React renders successfully
window.addEventListener('load', () => {
  // Give React a bit more time after window load
  setTimeout(() => {
    const rootElement = document.getElementById("root");
    if (rootElement && rootElement.children.length > 0) {
      clearBootTimeout();
    }
  }, 500);
});

const rootElement = document.getElementById("root");
if (rootElement) {
  try {
    const root = createRoot(rootElement);
    root.render(
      <HelmetProvider>
        <CartProvider>
          <App />
        </CartProvider>
      </HelmetProvider>
    );
    // Clear both React-side and HTML-side boot timeouts on successful render
    clearBootTimeout();
    // Clear HTML-side boot watchdog timeout
    if (typeof (window as unknown as { __clearBootTimeout?: () => void }).__clearBootTimeout === 'function') {
      (window as unknown as { __clearBootTimeout: () => void }).__clearBootTimeout();
    }
  } catch (e) {
    console.error("[Init] React render failed:", e);
    clearBootTimeout();
    rootElement.innerHTML = `
      <div style="min-height: 100vh; display: flex; align-items: center; justify-content: center; flex-direction: column; font-family: system-ui, -apple-system, sans-serif; padding: 20px; text-align: center; background: #fafafa;">
        <div style="max-width: 400px;">
          <h1 style="font-size: 1.5rem; margin-bottom: 12px; color: #1a1a1a;">Something went wrong</h1>
          <p style="color: #666; margin-bottom: 24px; line-height: 1.5;">
            We couldn't load the application. Please try refreshing the page.
          </p>
          <button onclick="window.location.reload()" style="padding: 12px 24px; background: #1a1a1a; color: white; border: none; border-radius: 8px; cursor: pointer; font-size: 14px;">
            Refresh Page
          </button>
        </div>
      </div>
    `;
  }
} else {
  console.error("[Init] Root element not found");
}

// Service worker registration is handled by vite-plugin-pwa via useRegisterSW
// No need for manual registration here - it causes duplicate registrations
